import {
  ApiError,
  type CreateOrderReturnResult,
  formatMoney,
  type Order,
  queryKeys,
  RETURNABLE_ORDER_STATUSES,
} from '@mevaser/shared'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api.js'

/** One returnable line — an order item with something still left to return. */
interface ReturnableItem {
  id: string
  productName: string
  sku: string
  unitPriceCents: number
  /** This line's own per-unit tax, exact: the order stamps `taxCents` as
   *  `round(unitTax) * qty` at checkout (t0947), so dividing back out by
   *  the original `qty` recovers it with no remainder. */
  unitTaxCents: number
  remaining: number
}

function clampBps(pct: string): number {
  const n = Number(pct)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.min(10_000, Math.round(n * 100))
}

/**
 * Item-level purchase returns with a percentage restocking fee (t0946).
 *
 * Modeled on `FulfilDialog` (stock-request-detail.tsx) for a line-item table
 * inside a dialog, and on `OrderTransitionDialog` for the shell/mutation —
 * but unlike either, every row here is its own small form: a line is
 * selected, how much of it, and what percentage to keep back. The math run
 * here to show a live preview is exactly what the server computes, so what
 * staff see before confirming is what actually happens.
 */
export function OrderReturnDialog({
  order,
  open,
  onClose,
}: {
  order: Order
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [qtyByItem, setQtyByItem] = useState<Record<string, string>>({})
  const [pctByItem, setPctByItem] = useState<Record<string, string>>({})
  const [note, setNote] = useState('')
  // One key per time the dialog opens — a doubled click or a slow network
  // retrying the same request replays onto the same return server-side
  // instead of refunding and restocking the same items twice.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID())

  const items: ReturnableItem[] = useMemo(
    () =>
      order.items
        .map((item) => ({
          id: item.id,
          productName: item.productName,
          sku: item.sku,
          unitPriceCents: item.unitPriceCents,
          unitTaxCents: item.qty > 0 ? item.taxCents / item.qty : 0,
          remaining: item.qty - item.returnedQty,
        }))
        .filter((item) => item.remaining > 0),
    [order.items],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-seed when the dialog opens, not on every order refetch — a background refetch while it's open must not blow away what staff already typed
  useEffect(() => {
    if (!open) return
    setSelected({})
    setQtyByItem(Object.fromEntries(items.map((i) => [i.id, String(i.remaining)])))
    setPctByItem(Object.fromEntries(items.map((i) => [i.id, '0'])))
    setNote('')
    setIdempotencyKey(crypto.randomUUID())
  }, [open])

  const lines = items
    .filter((item) => selected[item.id])
    .map((item) => {
      const remaining = item.remaining
      const qty = Math.min(remaining, Math.max(1, Math.round(Number(qtyByItem[item.id]) || 1)))
      const restockingFeeBps = clampBps(pctByItem[item.id] ?? '0')
      const grossCents = item.unitPriceCents * qty
      const feeCents = Math.round((grossCents * restockingFeeBps) / 10_000)
      const taxBackCents = Math.round(item.unitTaxCents * qty)
      const refundCents = grossCents - feeCents + taxBackCents
      return { item, qty, restockingFeeBps, grossCents, feeCents, taxBackCents, refundCents }
    })

  const totals = lines.reduce(
    (acc, l) => ({
      paidCents: acc.paidCents + l.grossCents + l.taxBackCents,
      feeCents: acc.feeCents + l.feeCents,
      refundCents: acc.refundCents + l.refundCents,
    }),
    { paidCents: 0, feeCents: 0, refundCents: 0 },
  )

  const willCompleteOrder =
    lines.length > 0 &&
    items.every((item) => selected[item.id] && Number(qtyByItem[item.id]) >= item.remaining)

  const submitReturn = useMutation({
    mutationFn: () =>
      api.post<CreateOrderReturnResult>(`/orders/${order.id}/returns`, {
        items: lines.map((l) => ({
          orderItemId: l.item.id,
          qty: l.qty,
          restockingFeeBps: l.restockingFeeBps,
        })),
        note: note.trim() || null,
        idempotencyKey,
      }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.units.all() }),
      ])
      // Stripe goes back to the card, gift card and store credit go back to the
      // card / account by themselves; only the rest is handed over.
      const handback = result.byProvider.filter(
        (p) =>
          p.provider !== 'stripe' && p.provider !== 'gift_card' && p.provider !== 'store_credit',
      )
      const toCredit = result.byProvider.filter(
        (p) => p.provider === 'gift_card' || p.provider === 'store_credit',
      )
      toast.toast({
        tone: 'success',
        title: `Returned — ${formatMoney(result.refundCents)} refunded`,
        description:
          handback.length > 0
            ? `Hand back ${handback.map((p) => formatMoney(p.refundCents)).join(', ')} at the counter.`
            : toCredit.length > 0
              ? 'Gift card / store credit was returned to its balance.'
              : undefined,
      })
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not return these items',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Return items — {order.orderNumber}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Alert tone="warning">
            Shipping is not refunded here — only the items selected below.
            {willCompleteOrder ? ' Returning every item on the order marks it Refunded.' : ''}
          </Alert>

          <table className="w-full text-sm">
            <thead className="border-b border-border text-2xs uppercase tracking-wide text-foreground-subtle">
              <tr>
                <th className="py-1.5 text-start font-medium" />
                <th className="py-1.5 text-start font-medium">Product</th>
                <th className="py-1.5 text-end font-medium">Qty</th>
                <th className="py-1.5 text-end font-medium">Restock %</th>
                <th className="py-1.5 text-end font-medium">Refund</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isSelected = Boolean(selected[item.id])
                const line = lines.find((l) => l.item.id === item.id)
                return (
                  <tr key={item.id} className="border-b border-border last:border-0">
                    <td className="py-1.5 pr-2 align-top">
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        checked={isSelected}
                        onChange={(e) =>
                          setSelected((current) => ({ ...current, [item.id]: e.target.checked }))
                        }
                      />
                    </td>
                    <td className="py-1.5 align-top">
                      <p className="font-medium">{item.productName}</p>
                      <p className="font-identifier text-2xs text-foreground-muted">{item.sku}</p>
                      {item.remaining > 1 ? (
                        <p className="text-2xs text-foreground-muted">{item.remaining} left</p>
                      ) : null}
                    </td>
                    <td className="py-1.5 text-end align-top">
                      {item.remaining > 1 ? (
                        <Input
                          className="w-16 text-end"
                          type="number"
                          min={1}
                          max={item.remaining}
                          disabled={!isSelected}
                          value={qtyByItem[item.id] ?? String(item.remaining)}
                          onChange={(e) =>
                            setQtyByItem((current) => ({ ...current, [item.id]: e.target.value }))
                          }
                        />
                      ) : (
                        <span className="tabular">1</span>
                      )}
                    </td>
                    <td className="py-1.5 text-end align-top">
                      <Input
                        className="w-20 text-end"
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        disabled={!isSelected}
                        value={pctByItem[item.id] ?? '0'}
                        onChange={(e) =>
                          setPctByItem((current) => ({ ...current, [item.id]: e.target.value }))
                        }
                      />
                    </td>
                    <td className="py-1.5 text-end align-top tabular font-medium">
                      {line ? formatMoney(line.refundCents) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {lines.length > 0 ? (
            <div className="space-y-1 border-t border-border pt-2 text-sm">
              <TotalRow label="Paid (incl. tax)" value={formatMoney(totals.paidCents)} />
              <TotalRow
                label="Restocking fee kept"
                value={`− ${formatMoney(totals.feeCents)}`}
                tone="danger"
              />
              <TotalRow label="Net refund" value={formatMoney(totals.refundCents)} strong />
            </div>
          ) : null}

          <Field label="Note" hint="Kept on the order history. The customer does not see it.">
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={lines.length === 0 || submitReturn.isPending}
            onClick={() => submitReturn.mutate()}
          >
            {submitReturn.isPending ? 'Working…' : `Refund ${formatMoney(totals.refundCents)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function TotalRow({
  label,
  value,
  strong,
  tone,
}: {
  label: string
  value: string
  strong?: boolean
  tone?: 'danger'
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-foreground-muted">{label}</span>
      <span
        className={`tabular ${strong ? 'font-semibold' : ''} ${tone === 'danger' ? 'text-danger' : ''}`}
      >
        {value}
      </span>
    </div>
  )
}

/** Whether the order is in a status an item-level return can be taken from,
 *  and something on it is still left to return. */
export function canReturnItems(order: Order): boolean {
  return (
    RETURNABLE_ORDER_STATUSES.includes(order.status) &&
    order.items.some((item) => item.qty > item.returnedQty)
  )
}
